/**
 * Turns a failed auth request into the error the designer specced.
 *
 * The flow deliberately hides *which* part of a credential pair was wrong and
 * never distinguishes expired / used / malformed reset tokens — both are
 * account-enumeration defences from Figma `66:47510` and `66:47519`. The mapper
 * is where that collapsing happens, so no view has to remember it.
 */

/** Where the message renders. Never feeds the submit button's disabled state. */
export type AuthErrorScope = 'form' | 'email' | 'password' | 'network'

export interface AuthError {
  scope: AuthErrorScope
  /** i18n key for the user-facing copy. */
  messageKey: string
  /** Original HTTP status, kept for logging only. */
  status?: number
}

export type AuthOperation = 'login' | 'signup' | 'forgotPassword' | 'resetPassword'

interface HttpErrorLike {
  response?: { status?: number, data?: unknown }
  message?: string
}

function readStatus(error: unknown): number | undefined {
  const status = (error as HttpErrorLike | null)?.response?.status
  return typeof status === 'number' ? status : undefined
}

/** Backends vary on where they put the reason; look in the usual places. */
function readDetail(error: unknown): string {
  const data = (error as HttpErrorLike | null)?.response?.data
  if (typeof data === 'string') return data.toLowerCase()
  if (data && typeof data === 'object') {
    const bag = data as Record<string, unknown>
    const parts = [bag.code, bag.detail, bag.message, bag.error]
      .filter((part): part is string => typeof part === 'string')
    return parts.join(' ').toLowerCase()
  }
  return ''
}

function looksLikeDuplicateEmail(error: unknown): boolean {
  const detail = readDetail(error)
  return /already|exist|registered|duplicate|taken/.test(detail)
}

/**
 * `/auth/reset-password` currently answers with a generic 400 for expired, used
 * and malformed tokens alike — see BE-021. Until it sends a stable code we treat
 * every 4xx on that call as "link no longer usable", which is what the single
 * Link expired screen shows anyway.
 */
export function isResetTokenRejected(error: unknown): boolean {
  const status = readStatus(error)
  return status !== undefined && status >= 400 && status < 500
}

export function mapAuthError(error: unknown, operation: AuthOperation): AuthError {
  const status = readStatus(error)

  // No response at all — dropped connection, DNS, CORS. Figma `66:47538`.
  if (status === undefined) {
    return { scope: 'network', messageKey: 'auth.errors.generic' }
  }

  if (status >= 500) {
    return { scope: 'network', messageKey: 'auth.errors.generic', status }
  }

  if (operation === 'login' && (status === 400 || status === 401 || status === 403)) {
    // One message for "no such user" and "wrong password" alike.
    return { scope: 'form', messageKey: 'auth.errors.invalid_credentials', status }
  }

  if (operation === 'signup' && (status === 409 || (status === 400 && looksLikeDuplicateEmail(error)))) {
    return { scope: 'email', messageKey: 'auth.errors.email_taken', status }
  }

  if (status === 429) {
    return { scope: 'network', messageKey: 'auth.errors.too_many_requests', status }
  }

  return { scope: 'network', messageKey: 'auth.errors.generic', status }
}
